import React, { useMemo } from "react";
import { Box, Typography } from "@mui/material";
import { ClipboardCheck, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SurveyRow from "@components/SurveyPickerCst/components/SurveyRow";
import { SurveyListItem } from "@components/SurveyPickerCst/types";
import AddItemsModalCst, {
  AddItemsConfirmEntry,
  AddItemsTabConfig,
} from "@components/AddItemsModalCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";

export type AddTestSurveyKind = "test" | "survey";

export type AddTestSurveySource = "models" | "activities";

export interface AddTestSurveySelectedItem {
  id: number;
  label: string;
  kind: AddTestSurveyKind;
}

interface AddTestSurveyModalCstProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selection: AddTestSurveySelectedItem[]) => void;
  initialTab?: AddTestSurveyKind;
  visibleTabs?: AddTestSurveyKind[];
  onCreate?: (tabKey: AddTestSurveyKind) => void;
  surveySource?: AddTestSurveySource;
}

const AddTestSurveyModalCst: React.FC<AddTestSurveyModalCstProps> = ({
  open,
  onClose,
  onConfirm,
  initialTab = "test",
  visibleTabs = ["test", "survey"],
  /* onCreate, */
  surveySource = "models",
}) => {
  const { t } = useTranslation("add-items-modal");
  const { id: corporateId = "" } = useParams();

  const activitiesUrl = corporateId
    ? ServicesURL.getActivities.replace(":idCorporate", corporateId)
    : null;
  const surveyUrl = corporateId
    ? `${ServicesURL.getSurvey}?corporateId=${corporateId}`
    : null;

  const useActivitiesData = (
    args: { page: number; pageSize: number; search?: string },
    type: string,
  ) => {
    const result = usePaginatedList<Record<string, any>>(activitiesUrl, {
      page: args.page,
      pageSize: args.pageSize,
      search: args.search,
      fieldFilters: { type, is_standalone: "false" },
    });
    const normalized: SurveyListItem[] = useMemo(
      () =>
        result.items.map((it) => ({
          id: Number(it.id),
          name: (it.title || it.name || "") as string,
        })),
      [result.items],
    );
    return { ...result, items: normalized };
  };

  const useTestData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) => useActivitiesData(args, "DA_XAPI,DA_SCORM");

  const useSurveyModelsData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) =>
    usePaginatedList<SurveyListItem>(
      surveyUrl,
      {
        page: args.page,
        pageSize: args.pageSize,
        search: args.search,
      },
      {
        responseKey: "content",
        totalElementsKey: "totalElements",
        totalPagesKey: "totalPages",
      },
    );

  const useSurveyActivitiesData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) => useActivitiesData(args, "DA_SURVEY");

  const renderRow = ({ item }: { item: SurveyListItem }) => (
    <SurveyRow item={item} />
  );

  const allTabs: AddItemsTabConfig<SurveyListItem>[] = [
    {
      key: "test",
      label: String(t("tabs.tests") ?? "Test"),
      icon: <ClipboardCheck size={14} />,
      useDataHook: useTestData,
      renderRow,
      getId: (it) => it.id,
    },
    {
      key: "survey",
      label: String(t("tabs.surveys") ?? "Survey"),
      icon: <FileText size={14} />,
      useDataHook:
        surveySource === "activities"
          ? useSurveyActivitiesData
          : useSurveyModelsData,
      renderRow,
      getId: (it) => it.id,
    },
  ];

  const tabs = allTabs.filter((tabCfg) =>
    visibleTabs.includes(tabCfg.key as AddTestSurveyKind),
  );

  const handleConfirm = (entries: AddItemsConfirmEntry<SurveyListItem>[]) => {
    onConfirm(
      entries.map((e) => ({
        id: e.item.id,
        label: e.item.name,
        kind: e.tabKey as AddTestSurveyKind,
      })),
    );
    onClose();
  };

  const renderSubtitle =
    tabs.length > 1
      ? (countByTab: Record<string, number>) => (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography sx={{ fontSize: "16px" }}>
              {String(
                t("test-survey.disclaimer") ??
                  "Puoi aggiungere sia test che survey, fino ad un massimo di 1 per tipologia",
              )}
            </Typography>
            <Typography sx={{ fontSize: "12px" }}>
              {String(t("test-survey.adding") ?? "Stai aggiungendo:")}{" "}
              <Box component="span" sx={{ fontWeight: 700 }}>
                {String(
                  t("test-survey.count-test", {
                    count: countByTab.test ?? 0,
                  }) ?? `${countByTab.test ?? 0}/1 Test`,
                )}
                {" | "}
                {String(
                  t("test-survey.count-survey", {
                    count: countByTab.survey ?? 0,
                  }) ?? `${countByTab.survey ?? 0}/1 Survey`,
                )}
              </Box>
            </Typography>
          </Box>
        )
      : undefined;

  return (
    <AddItemsModalCst<SurveyListItem>
      open={open}
      onClose={onClose}
      title={String(t("title.add-test-survey") ?? "Aggiungi Test e Survey")}
      selectionMode="single"
      allowDeselect
      renderSubtitle={renderSubtitle}
      tabs={tabs}
      initialTab={initialTab}
      onConfirm={handleConfirm}
      /* onCreate={
        onCreate ? (tab) => onCreate(tab as AddTestSurveyKind) : undefined
      } */
      confirmLabel={String(t("confirm") ?? "Aggiungi")}
      createLabel={String(t("create") ?? "Crea")}
    />
  );
};

export default AddTestSurveyModalCst;
